import { useWaWorkplaceStore } from 'src/api/wa/store/WaWorkplaceStore';
import { ButtonProps } from '../../common/button/Button';
import PropertiesHeader from '../../common/propertiesHeader/PropertiesHeader';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import LinkButton from '../../common/button/LinkButton';
import { useEffect, useState } from 'react';
import useWaWorkplaceService from 'src/api/wa/service/WaWorkplaceService';
import Alert from 'src/ui/common/alert/Alert';
import { WaScreensMap } from '../../router/WardsScreenMap';
import { WA_Screen } from '../../router/WardsScreens';
import { wardsNavigate } from '../../router/WardsScreenRegistration';
import { getDetailStatusTranslationKey } from 'src/api/wa/store/models/workplace/OperationStatus';
import { useWaUserStore } from 'src/api/wa/store/WaUserStore';
import { useWaFilterStore } from 'src/api/wa/store/WaFilterStore';
import { useWaLocationService } from 'src/api/wa/service/WaLocationService';
import { Workplace, WorkplaceReservationToCreate } from 'src/api/wa/store/models/workplace';
import { FormRow } from '../../common/form/FormRow';
import { useNotify } from 'src/ui/common/alert/useNotify';
import { LoadingSpinner } from '../../desktop/components/common/loading/LoadingSpinner';

const WorkplaceListItem: React.FC<{
  site: Workplace;
  isSelected: boolean;
  onSelect: (site: Workplace) => void;
  onDetailsClick: (site: Workplace) => void;
}> = ({ site, isSelected, onSelect, onDetailsClick }) => {
  const { t } = useTranslation();

  return (
    <div
      className={`flex gap-2 border-t border-gray-300 px-4 py-3 ${isSelected ? 'bg-sky-200' : ''}`}
    >
      {onSelect && (
        <div className="flex items-center pt-2">
          <input type="checkbox" checked={!!isSelected} onChange={() => onSelect(site)} />
        </div>
      )}
      <div className="w-full space-y-3 pl-2">
        <FormRow title={t('wards.common.customerNumber')} layout="vertical" className="text-sm">
          <LinkButton title={site.customerId} onClick={() => onDetailsClick(site)} />
        </FormRow>
        <FormRow title={t('wards.common.status')} layout="vertical" className="text-sm">
          <span>
            {site.details?.statusId ? t(getDetailStatusTranslationKey(site.details?.statusId)) : ''}
          </span>
        </FormRow>
        <FormRow title={t('wards.sites.buildingName')} layout="vertical" className="text-sm">
          <span>
            {site.emergencyClass ? '★' : ''} {site.name}
          </span>
        </FormRow>
        <FormRow title={t('wards.siteDetails.comment')} layout="vertical" className="text-sm">
          <span>
            {site.details?.comment ? t('wards.common.withComment') : t('wards.common.noComment')}
          </span>
        </FormRow>
      </div>
    </div>
  );
};

function S3_3_UnaddressedPropertiesList() {
  const { workplaces, setSelectedWorkplace } = useWaWorkplaceStore();
  const { createWorkplaceReservationWithoutRefresh, fetchWorkplaces } = useWaWorkplaceService();
  const { user } = useWaUserStore();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const { areMandatorySearchParametersSet } = useWaFilterStore();
  const { updateWaUserLocation } = useWaLocationService();
  const [selectedWorkplaces, setSelectedWorkplaces] = useState<Workplace[]>([]);
  const [showErrorReservation, setShowErrorReservation] = useState(false);
  const [errorWorkplaces, setErrorWorkplaces] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTimeout, setIsTimeout] = useState(false);
  const notify = useNotify();
  // Limit to max 100 records as per specification
  const resolvedWorkplaces = workplaces ? workplaces.slice(0, 100) : [];
  const shouldShowNoSitesMessage =
    !isLoading &&
    !isTimeout &&
    workplaces !== null &&
    areMandatorySearchParametersSet() &&
    resolvedWorkplaces.length === 0;

  const clearSelectedWorkplaces = () => setSelectedWorkplaces([]);

  const handleRefreshClick = async () => {
    clearSelectedWorkplaces();
    setIsLoading(true);
    setIsTimeout(false);
    try {
      await fetchWorkplaces();
    } catch (error) {
      if ((error as { code?: string }).code === 'ECONNABORTED') {
        setIsTimeout(true);
      }
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handle = (site: Workplace) => {
    setSelectedWorkplaces((prev) => {
      const isAlreadySelected = prev.includes(site);

      if (isAlreadySelected) {
        return prev.filter((item) => item !== site);
      } else {
        if (prev.length >= 20) {
          notify(t('wards.common.reservationLimitExceeded'));
          return prev;
        }
        return [...prev, site];
      }
    });
  };

  const handleReservedPropertiesClick = () => {
    wardsNavigate(navigate, WaScreensMap[WA_Screen.AddressedWorkplacesList].path);
  };

  const isReservationDisabled = selectedWorkplaces.length > 20;

  const handleReservation = async () => {
    setIsLoading(true);
    setShowErrorReservation(false);
    setShowSuccessPopup(false);

    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    const localErrors: string[] = [];
    const localSkipped: string[] = [];

    await Promise.all(
      selectedWorkplaces.map(async (site) => {
        const reservationsToCreate: WorkplaceReservationToCreate[] = [];

        if (site.details?.reservations?.length == 0) {
          reservationsToCreate.push({
            engineerUserId: user.id,
            customerId: site.customerId,
            branchCode: site.branchCode,
          });

          try {
            const res = await createWorkplaceReservationWithoutRefresh(reservationsToCreate);
            if (!res) {
              localErrors.push(site.customerId);
            }
          } catch (e) {
            console.warn(e);
            localErrors.push(site.customerId);
          }
        } else {
          localSkipped.push(site.customerId);
        }
      })
    );

    setErrorWorkplaces(localErrors);

    if (localErrors.length > 0) {
      setShowErrorReservation(true);
    } else if (selectedWorkplaces.length === localSkipped.length) {
      notify('wards.sites.allAlreadyReserved');
    } else {
      setShowSuccessPopup(true);
    }

    updateWaUserLocation();
    setSelectedWorkplaces([]);
    try {
      await fetchWorkplaces();
    } catch (error) {
      if ((error as { code?: string }).code === 'ECONNABORTED') {
        setIsTimeout(true);
      }
      console.error(error);
    }
    setIsLoading(false);
  };

  const buttons: ButtonProps[] = [
    {
      key: 'refresh',
      title: t('wards.common.refresh'),
      onClick: handleRefreshClick,
    },
    {
      key: 'reservedPropertyList',
      title: t('wards.header.reservedPropertyList'),
      onClick: handleReservedPropertiesClick,
    },
    {
      key: 'reserve',
      title: t('wards.common.reserve'),
      onClick: handleReservation,
      disabled: isReservationDisabled || selectedWorkplaces.length === 0,
    },
    {
      key: 'menu',
      title: t('wards.common.menu'),
      onClick: () => {
        wardsNavigate(navigate, WaScreensMap[WA_Screen.RecoverySupportMenu].path);
      },
    },
    {
      key: 'changeSearchConditions',
      title: t('wards.header.changeSearchCondition'),
      onClick: () => wardsNavigate(navigate, WaScreensMap[WA_Screen.SearchFilter].path),
      disabled: false,
    },
    {
      key: 'clearAll',
      title: t('wards.common.clearAll'),
      onClick: clearSelectedWorkplaces,
    },
  ];

  const handleDetailsClick = (site: Workplace) => {
    setSelectedWorkplace(site);
    wardsNavigate(navigate, WaScreensMap[WA_Screen.WorkplaceDetails].path);
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setIsTimeout(false);
      try {
        await fetchWorkplaces();
      } catch (error) {
        if ((error as { code?: string }).code === 'ECONNABORTED') {
          setIsTimeout(true);
        }
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [fetchWorkplaces]);

  return (
    <div className="bg-gray-100 pb-20">
      <PropertiesHeader
        title={t('wards.supportMenu.unaddressedPropertiesList')}
        buttons={buttons}
      />
      {showErrorReservation && (
        <p className="mb-4 text-center text-sm text-red-600">
          {t('wards.sites.reservationFailed', {
            workplaces: errorWorkplaces.join(', '),
          })}
        </p>
      )}
      {isReservationDisabled && (
        <p className="mb-4 text-center text-sm text-red-600">
          {t('wards.common.reservationLimitExceeded')}
        </p>
      )}
      {showSuccessPopup && (
        <Alert
          message={t('wards.sites.reservationSuccess')}
          onClose={() => setShowSuccessPopup(false)}
        />
      )}
      <div className="relative">
        {isLoading ? (
          <div className="my-10 flex flex-col items-center justify-start">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {shouldShowNoSitesMessage && (
              <h3 className="mx-2 mb-8 text-center text-xl">{t('wards.common.noResults')}</h3>
            )}
            {isTimeout && (
              <h3 className="mx-2 mb-8 text-center text-xl text-red-500">
                {t('wards.common.searchTimeout')}
              </h3>
            )}
            <div>
              {resolvedWorkplaces.map((site) => (
                <WorkplaceListItem
                  key={site.customerId}
                  site={site}
                  isSelected={selectedWorkplaces.includes(site)}
                  onSelect={handle}
                  onDetailsClick={handleDetailsClick}
                />
              ))}
            </div>
          </>
        )}
      </div>
      <PropertiesHeader title="" buttons={buttons} />
    </div>
  );
}

export default S3_3_UnaddressedPropertiesList;
